import { useState, useEffect } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import moment from 'moment';
import toast from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

import {
  Select,
  Avatar,
  Button,
  MultiSelect,
  ConfirmationAlertModal,
} from 'src/components';
import SearchResultItem from './SearchResultItem';
import ProgressBar from './ProgressBar';
import GridIcon from 'src/assets/images/Grid-icon.svg';
import GridBlackIcon from 'src/assets/images/Grid-black-icon.svg';
import ColumnBlackIcon from 'src/assets/images/Column-black-icon.svg';
import ColumnIcon from 'src/assets/images/Column-icon.svg';
import ArrowUpIcon from 'src/assets/images/arrow-up-icon.svg';
import ArrowDownIcon from 'src/assets/images/Arrows-down.svg';
import EditIcon from 'src/assets/images/Edit-icon.svg';
import DroppableArea from './DroppableArea';
import DropdownLoadingSkeleton from './DropdownLoadingSkeleton';
import ProjectSkeletonCard from './ProjectSkeletonCard';
import EditRequirementModal from './EditRequirementModal';
import { getColorForActivityLevel, projectStatusColors } from 'src/constants';
import CloseFilledIcon from 'src/assets/images/close-filled.svg';
import http from 'src/api/endpoints';
import useLoading from 'src/hooks/useLoading';
import { PARENT_URL } from 'src/config/parentURL';
import { getAllProjects } from 'src/redux/projectSlice';
import { AppDispatch } from 'src/redux/store';
import { twMerge } from 'tailwind-merge';

type ProjectsProps = {
  allProjects: any[];
  isProjectsLoading: boolean;
  projectStatusFilter: any[];
  allProjectName: any[];
  projectStaffingStatus: any[];
};

interface IProjectId {
  projectId: string;
  staffingId: string;
  project_deadline: string;
}

interface IFilters {
  selectedStaffingStatus: string | number;
  selectedProjectStatus: string | number;
  multipleProjectNames: any[] | null;
}

const initialFilterObject: IFilters = {
  selectedStaffingStatus: 'false',
  selectedProjectStatus: '',
  multipleProjectNames: null,
};

const Projects = ({
  allProjects,
  isProjectsLoading,
  projectStatusFilter,
  allProjectName,
  projectStaffingStatus,
}: ProjectsProps) => {
  const dispatch: AppDispatch = useDispatch();
  const [isGrid, setIsGrid] = useState<boolean>(true);
  const [activeCardsIndexes, setActiveCardsIndexes] = useState<number[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isDropActive, setIsDropActive] = useState<boolean>(false);
  const [projectData, setProjectData] = useState<any[]>(allProjects);
  const [addedStaff, setAddedStaff] = useState<any[]>([]);
  const [removedStaff, setRemovedStaff] = useState<any[]>([]);
  const [editProjectId, setEditProjectId] = useState<IProjectId>({
    projectId: '',
    staffingId: '',
    project_deadline: '',
  });
  const [filters, setFilters] = useState<IFilters>({ ...initialFilterObject });
  const [removeMember, setRemoveMember] = useState<{
    isOpen: boolean;
    data: {
      index: number | null;
      staffData: {};
      project: {};
    };
  }>({
    isOpen: false,
    data: {
      index: null,
      staffData: {},
      project: {},
    },
  });
  const {
    isLoading: isUpdateProjectLoading,
    startLoading: updateProjectStartLoading,
    stopLoading: updateProjectStopLoading,
  } = useLoading();

  useEffect(() => {
    if (allProjects?.length) {
      setProjectData(allProjects);
    }
  }, [allProjects]);

  useEffect(() => {
    setFilters(() => ({
      ...initialFilterObject,
      selectedStaffingStatus: 'false',
    }));
    const copyData = [...allProjects];
    const filterData = copyData?.filter(
      (project) => project.isStaffingComplete === false
    );
    setProjectData(filterData);
  }, [allProjects]);

  const onHandleCardActive = () => {
    setIsGrid(!isGrid);
  };

  const handleCardClick = (index: number) => {
    setActiveCardsIndexes((prevIndexes) => {
      if (prevIndexes?.includes(index)) {
        return prevIndexes?.filter((prevIndex) => prevIndex !== index);
      } else {
        return [...prevIndexes, index];
      }
    });
  };

  const onHandleDropMember = (
    data: any,
    index: number,
    project: any,
    roleId: string,
    staffingRequirementId: string,
    staffInnerIndex?: number,
    state?: string
  ) => {
    if (state !== undefined && state !== 'accepted') {
      toast.error('You can not replace a member who has a pending invitation');
      setIsDropActive(false);
      return;
    }
    if (roleId === data.role.id) {
      const checkIfUserExist = project?.staffingRequirements.find(
        (staff: any) => {
          if (staff.roleId === roleId) {
            // Check if the user already exists in the staffing requirements for this role
            return staff.numberOfStaffedUsers?.find(
              (eStaff: any) => eStaff.userId === data.id
            );
          }
        }
      );

      if (checkIfUserExist !== undefined) {
        toast.error('The user assigned to this role already exists.');
        setIsDropActive(false);
        return;
      }

      const updatedStaffingRequirements = project?.staffingRequirements.map(
        (staff: any) => {
          if (staff.roleId === roleId) {
            let updatedStaffedUsers: any[] = [...staff.numberOfStaffedUsers];
            if (
              staff.numberOfStaffedUsers.length === 0 ||
              staff.numberOfStaffedUsers === null ||
              staffInnerIndex === undefined
            ) {
              updatedStaffedUsers = [
                ...staff.numberOfStaffedUsers,
                {
                  role: data.role,
                  role_on_project: data?.role?.id,
                  staffingRequirementId: staff.staffingRequirementId,
                  user: {
                    avatar: data.avatar,
                    email: data.email,
                    id: data.id,
                    name: data.name,
                  },
                  userId: data.id,
                  isDropped: true,
                  state: 'accepted',
                },
              ];
              setAddedStaff((prevAddedStaff) => [
                ...prevAddedStaff,
                {
                  staffingRequirementId: staffingRequirementId,
                  userId: data.id,
                  projectId: project.id,
                },
              ]);
              return {
                ...staff,
                numberOfStaffedUsers: updatedStaffedUsers,
                numberOfRemainingUsersToBeStaffed:
                  staff.numberOfRemainingUsersToBeStaffed - 1,
              };
            } else {
              setRemovedStaff([
                ...removedStaff,
                {
                  staffingRequirementId:
                    updatedStaffedUsers[staffInnerIndex as number]
                      ?.staffingRequirementId,
                  userId:
                    updatedStaffedUsers[staffInnerIndex as number]?.userId,
                  projectId: project.id,
                },
              ]);
              setAddedStaff([
                ...addedStaff,
                {
                  staffingRequirementId: staffingRequirementId,
                  userId: data.id,
                  projectId: project.id,
                },
              ]);
              updatedStaffedUsers[staffInnerIndex as number] = {
                role: data.role,
                role_on_project: data?.role?.id,
                staffingRequirementId: staff.staffingRequirementId,
                user: {
                  avatar: data.avatar,
                  email: data.email,
                  id: data.id,
                  name: data.name,
                },
                userId: data.id,
              };
              return {
                ...staff,
                numberOfStaffedUsers: updatedStaffedUsers,
              };
            }
          }
          return staff;
        }
      );

      const updatedProject = {
        ...project,
        staffingRequirements: updatedStaffingRequirements,
        totalNumOfRequiredUsers: project.totalNumOfRequiredUsers - 1,
        totalInvited: project.totalInvited + 1,
      };

      setProjectData((prevState) =>
        prevState.map((item, i) => {
          if (index === i) {
            return {
              ...updatedProject,
            };
          }
          return item;
        })
      );
    } else {
      toast.error('The member selected does not match the role.');
    }

    setIsDropActive(false);
  };

  const onHandleRemoveMember = (
    index: number | null,
    staff: any,
    project: any
  ) => {
    const { role_on_project, userId, staffingRequirementId } = staff || {};

    const updatedStaffingRequirements = project?.staffingRequirements.map(
      (staff: any) => {
        if (staff.roleId === role_on_project) {
          const existingUserIndex = staff.numberOfStaffedUsers.findIndex(
            (user: any) => user.userId === userId
          );
          const updatedStaffedUsers = staff.numberOfStaffedUsers.filter(
            (user: any, index: number) => index !== existingUserIndex
          );
          return {
            ...staff,
            numberOfRemainingUsersToBeStaffed:
              staff.numberOfRemainingUsersToBeStaffed + 1,
            numberOfStaffedUsers: updatedStaffedUsers,
          };
        }
        return staff;
      }
    );

    const updatedProject = {
      ...project,
      staffingRequirements: updatedStaffingRequirements,
      totalInvited: project.totalInvited - 1,
    };

    const newAddedStaff = addedStaff?.filter(
      (staff) => staff.staffingRequirementId !== staffingRequirementId
    );
    setAddedStaff(newAddedStaff);

    setProjectData((prevState) =>
      prevState.map((item, i) => {
        if (index === i) {
          return {
            ...updatedProject,
          };
        }
        return item;
      })
    );
    setRemovedStaff((prevRemovedStaff) => [
      ...prevRemovedStaff,
      {
        staffingRequirementId,
        userId,
        projectId: project.id,
      },
    ]);
    setRemoveMember({
      isOpen: false,
      data: {
        index: null,
        project: {},
        staffData: {},
      },
    });
  };

  const getFullTimeTerms = (value: string) => {
    if (value === 'part_time') {
      return 'Part Time';
    } else if (value === 'full_time') {
      return 'Full Time';
    } else {
      return '';
    }
  };

  const getFromUntilDates = (from: string, until: string) => {
    const fromDate = moment(from).format('DD MMM');
    const untilDate = moment(until).format('DD MMM, YY');
    return `${fromDate}-${untilDate}`;
  };

  const onHandleUpdateProject = async () => {
    const data = {
      remove: removedStaff,
      assigned: addedStaff,
    };
    updateProjectStartLoading();
    try {
      const response = await http.updateProjects(
        PARENT_URL.TEAM_BUILDER_BASE,
        data
      );
      if (response.message === 'The request is completed successfully') {
        updateProjectStopLoading();
        dispatch(getAllProjects());
        setAddedStaff([]);
        setRemovedStaff([]);
        return;
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          'Failed to update Projects. Please try again later.'
      );
    } finally {
      updateProjectStopLoading();
    }
  };

  const resetState = () => {
    setActiveCardsIndexes([]);
    setAddedStaff([]);
    setRemovedStaff([]);
  };

  useEffect(() => {
    const { multipleProjectNames } = filters || {};
    if (multipleProjectNames !== null && multipleProjectNames?.length) {
      const searchLower = multipleProjectNames?.map((name: any) =>
        name?.value?.toLowerCase()
      );
      const results = allProjects?.filter((project) => {
        return searchLower?.some(
          (term: any) => project?.projectCode?.toLowerCase() === term
        );
      });
      setProjectData(results);
    }
  }, [filters, allProjects]);

  useEffect(() => {
    const {
      selectedProjectStatus,
      selectedStaffingStatus,
      multipleProjectNames,
    } = filters || {};
    if (multipleProjectNames === null) {
      let filteredData = [...allProjects];
      if (selectedStaffingStatus !== 'all') {
        filteredData = filteredData?.filter(
          (project) =>
            project?.isStaffingComplete ===
            (selectedStaffingStatus === 'true' ? true : false)
        );
      }
      if (selectedStaffingStatus === '') {
        filteredData = allProjects;
      }
      if (selectedProjectStatus && selectedProjectStatus !== 'all') {
        filteredData = filteredData.filter(
          (project) => project?.status?.rank === selectedProjectStatus
        );
      }
      setProjectData(filteredData);
    }
  }, [filters, allProjects]);

  const resetFilters = () => {
    resetState();
    setFilters(initialFilterObject);
    const copyData = [...allProjects];
    const filterData = copyData?.filter(
      (project) => project.isStaffingComplete === false
    );
    setProjectData(filterData);
  };

  const onHandleRemoveFilterResult = (id: string) => {
    const filterData = projectData?.filter((item) => item?.id !== id);
    setProjectData(filterData);
  };

  return (
    <div className="pt-6 pr-3 team-builder">
      <div className="text-blue-950 text-lg font-medium font-SFProDisplay space-x-3">
        <span>Projects</span>
        <span className="text-[13px] font-normal">
          (Drop a team member from the list of members onto the relevant role in
          a project.)
        </span>
      </div>
      <div className="mt-[18px] bg-gray-50 py-4 px-6 flex flex-col">
        <div className="flex justify-between items-center flex-wrap-reverse  md:mb-[14px]">
          <div>
            {isProjectsLoading ? (
              <Skeleton
                height={30}
                width={350}
                style={{ borderRadius: '20px' }}
              />
            ) : (
              <MultiSelect
                options={allProjectName}
                handleChange={(option) =>
                  setFilters(() => ({
                    ...initialFilterObject,
                    selectedStaffingStatus: '',
                    multipleProjectNames: option,
                  }))
                }
                value={filters.multipleProjectNames}
                error=""
                className="md:min-w-[370px] min-h-9 "
                placeholder="Search Project by Name"
              />
            )}
          </div>
          <div
            className="text-indigo-600 text-sm font-medium font-SFProDisplay cursor-pointer"
            onClick={resetFilters}
          >
            {isProjectsLoading ? (
              <Skeleton height={18} width={100} />
            ) : (
              'RESET FILTERS'
            )}
          </div>
        </div>
        <div className="flex gap-6 items-center flex-wrap">
          <div className="flex items-center gap-2 xs:mt-5">
            {isProjectsLoading ? (
              <DropdownLoadingSkeleton
                isLabel={false}
                style={{ marginTop: '10px' }}
              />
            ) : (
              <>
                <div className="text-blue-950 text-sm font-medium font-SFProDisplay">
                  Project Status:
                </div>
                <Select
                  options={projectStatusFilter}
                  value={filters.selectedProjectStatus}
                  className="w-[156px] h-8"
                  optionClasses="text-sm text-slate-500 font-SFProDisplay font-medium "
                  placeholder="All"
                  onChange={(value: string | number) => {
                    if (value !== filters.selectedProjectStatus) {
                      setFilters((prevState) => ({
                        ...prevState,
                        multipleProjectNames: null,
                        selectedProjectStatus: value,
                      }));
                    }
                  }}
                />
              </>
            )}
          </div>
          <div className="flex items-center gap-2 xs:mt-[-10px]">
            {isProjectsLoading ? (
              <DropdownLoadingSkeleton isLabel={false} />
            ) : (
              <>
                <div className="text-blue-950 text-sm font-medium font-SFProDisplay">
                  Staffing Status:
                </div>
                <Select
                  options={projectStaffingStatus}
                  value={filters.selectedStaffingStatus}
                  className="w-[156px] h-8"
                  optionClasses="text-sm text-slate-500 font-SFProDisplay font-medium "
                  placeholder="All"
                  onChange={(value: string | number) => {
                    setFilters((prevState) => ({
                      ...prevState,
                      multipleProjectNames: null,
                      selectedStaffingStatus: value,
                    }));
                  }}
                />
              </>
            )}
          </div>
        </div>
        {filters.selectedProjectStatus &&
        filters.selectedProjectStatus !== 'all' ? (
          <div className="mt-[14px] flex gap-2 flex-wrap">
            {projectData.map((project) => (
              <SearchResultItem
                text={project.projectCode}
                id={project.id}
                onClick={(id: string) => onHandleRemoveFilterResult(id)}
              />
            ))}
          </div>
        ) : null}
      </div>
      <div className="mt-5 flex justify-between items-center mb-4">
        <div className="flex items-center gap-4">
          {isProjectsLoading ? (
            <Skeleton height={18} width={50} />
          ) : (
            <div
              className="flex items-center gap-[6px] cursor-pointer"
              onClick={() => onHandleCardActive()}
            >
              <img
                src={isGrid ? GridIcon : GridBlackIcon}
                alt="grid icon"
                className="w-4 h-4"
              />
              <p
                className={`text-sm font-medium font-SFProDisplay ${
                  isGrid ? 'text-indigo-600' : 'text-slate-500'
                }`}
              >
                Grid
              </p>
            </div>
          )}
          {isProjectsLoading ? (
            <Skeleton height={18} width={50} />
          ) : (
            <div
              className="flex items-center gap-[6px] cursor-pointer"
              onClick={() => onHandleCardActive()}
            >
              <img
                src={!isGrid ? ColumnIcon : ColumnBlackIcon}
                alt="grid icon"
                className="w-4 h-4"
              />
              <p
                className={`text-sm font-medium font-SFProDisplay ${
                  isGrid ? 'text-slate-600' : 'text-indigo-500'
                }`}
              >
                Columns
              </p>
            </div>
          )}
        </div>
        {isProjectsLoading ? (
          <Skeleton height={30} width={90} style={{ borderRadius: '20px' }} />
        ) : (
          <Button
            variant="normal"
            size="sm"
            label="Save"
            className="block w-22 h-8"
            type="button"
            disabled={
              (addedStaff.length === 0 && removedStaff.length === 0) ||
              isUpdateProjectLoading
            }
            onClick={onHandleUpdateProject}
            isLoading={isUpdateProjectLoading}
            loaderClassName="h-5"
          />
        )}
      </div>
      <div className="overflow-y-auto max-h-[55vh]">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 gap-x-6 gap-y-4  mb-4">
          {isProjectsLoading
            ? Array.from({ length: 4 }).map((_, i) => (
                <ProjectSkeletonCard key={i} index={i} />
              ))
            : projectData?.map((project, index) => {
                const {
                  projectCode,
                  status,
                  projectSubFocus,
                  staffingRequirements,
                  percentage = 0,
                  id: project_id,
                  deadline: project_deadline,
                } = project || {};

                const getCapacityBackgroundColor =
                  getColorForActivityLevel(percentage);
                const projectStatusColor = projectStatusColors[status?.rank];
                return (
                  <div
                    key={index}
                    className={twMerge(
                      'border border-zinc-200  rounded-lg font-SFProDisplay',
                      activeCardsIndexes?.includes(index) || !isGrid
                        ? 'min-h-[75px] '
                        : ' pb-4 min-h-[75px]'
                    )}
                  >
                    <div className="px-6 pt-4">
                      <div className="flex justify-between">
                        <div className="flex items-start gap-2">
                          <div className="text-blue-950 text-base font-bold uppercase overflow-hidden whitespace-nowrap text-ellipsis">
                            {projectCode}
                          </div>
                          <div
                            className="text-sm font-normal mt-[1px] capitalize"
                            style={{
                              color: projectStatusColor?.textColor || '#0B1B4E',
                            }}
                          >
                            {status?.name}
                          </div>
                        </div>
                        <div className="flex gap-6">
                          <div className="items-start">
                            <p className="text-blue-950 text-xs font-normal pb-[3px]">
                              Staffed:{' '}
                              <span
                                className="font-medium "
                                style={{ color: getCapacityBackgroundColor }}
                              >
                                {`${percentage}%`}
                              </span>
                            </p>
                            <ProgressBar
                              backgroundColor={getCapacityBackgroundColor}
                              percentage={percentage}
                            />
                          </div>
                          {isGrid ? (
                            <img
                              src={
                                activeCardsIndexes?.includes(index)
                                  ? ArrowUpIcon
                                  : ArrowDownIcon
                              }
                              alt="arrow icon"
                              className={twMerge(
                                'cursor-pointer mt-[-14px] transform transition-transform duration-300 ease-in-out',
                                activeCardsIndexes?.includes(index)
                                  ? 'rotate-180'
                                  : 'rotate-0'
                              )}
                              onClick={() => handleCardClick(index)}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div
                        className={twMerge(
                          'flex flex-wrap gap-2 mt-[-12px]',
                          activeCardsIndexes?.includes(index) || !isGrid
                            ? 'mb-4'
                            : ''
                        )}
                        key={index}
                      >
                        {projectSubFocus?.length ? (
                          <>
                            {projectSubFocus
                              ?.slice(0, 3)
                              ?.map((type: string, i: number) => (
                                <div
                                  key={i}
                                  className=" h-4 px-[5px] py-1 bg-white rounded-lg border border-gray-300 justify-center items-center gap-2.5 inline-flex"
                                >
                                  <div className="text-slate-500 text-xs font-normal font-SFProDisplay capitalize">
                                    {type}
                                  </div>
                                </div>
                              ))}
                          </>
                        ) : null}
                      </div>
                    </div>
                    {activeCardsIndexes?.includes(index) || !isGrid ? (
                      <div className="bg-gray-50 mt-3 border-t border-t-zinc-200 font-SFProDisplay px-6 pb-5 pt-5">
                        {staffingRequirements.length ? (
                          staffingRequirements?.map((staff: any, i: number) => (
                            <>
                              <div
                                className="flex items-center justify-between"
                                key={i}
                              >
                                <div className="flex gap-2 items-center">
                                  <p className="text-blue-950 text-sm font-medium ">
                                    {staff?.roleName}{' '}
                                    {`(${staff?.numberOfStaffedUsers?.length}/${staff?.numberOfRequiredUsers})`}
                                  </p>
                                  <p className="text-slate-500 text-xs font-medium">
                                    {`${getFullTimeTerms(
                                      staff?.time
                                    )} | SE 2 (${getFromUntilDates(
                                      staff?.fromDate,
                                      staff?.untilDate
                                    )})`}
                                  </p>
                                </div>
                                <img
                                  src={EditIcon}
                                  alt="edit icon"
                                  className="w-3 h-3 cursor-pointer"
                                  onClick={() => {
                                    setIsModalOpen(true);
                                    setEditProjectId({
                                      projectId: project_id,
                                      staffingId: staff.staffingRequirementId,
                                      project_deadline: project_deadline,
                                    });
                                  }}
                                />
                              </div>
                              <div className="mt-3 mb-3">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 font-SFProDisplay">
                                  {staff?.numberOfStaffedUsers?.map(
                                    (
                                      staffData: any,
                                      staffInnerIndex: number
                                    ) => (
                                      <DndProvider
                                        backend={HTML5Backend}
                                        key={staffInnerIndex}
                                      >
                                        <DroppableArea
                                          onDrop={(e) =>
                                            onHandleDropMember(
                                              e,
                                              index,
                                              project,
                                              staff.roleId,
                                              staff.staffingRequirementId,
                                              staffInnerIndex,
                                              staffData.state
                                            )
                                          }
                                          onDragOver={() => {
                                            setIsDropActive(true);
                                          }}
                                          onDragLeave={() => {
                                            setIsDropActive(false);
                                          }}
                                        >
                                          <div
                                            className="relative group"
                                            key={staffInnerIndex}
                                          >
                                            {staffData.state === 'accepted' ? (
                                              <img
                                                src={CloseFilledIcon}
                                                alt="lose filled icon"
                                                className="absolute right-[-5px] top-[-10px] opacity-0 transition-opacity duration-300 group-hover:opacity-100 cursor-pointer"
                                                onClick={() => {
                                                  if (
                                                    staffData.state ===
                                                    'accepted'
                                                  ) {
                                                    if (staffData.isDropped) {
                                                      onHandleRemoveMember(
                                                        index,
                                                        staffData,
                                                        project
                                                      );
                                                    } else {
                                                      setRemoveMember({
                                                        isOpen: true,
                                                        data: {
                                                          index,
                                                          staffData,
                                                          project,
                                                        },
                                                      });
                                                    }
                                                  }
                                                }}
                                              />
                                            ) : null}

                                            <div
                                              className={` h-10 rounded-md shadow-md bg-white py-2 px-3 flex items-center justify-between ${
                                                staffData.state !==
                                                  'accepted' && 'bg-opacity-30'
                                              }`}
                                            >
                                              <Avatar
                                                url={staffData?.user?.avatar}
                                                name={staffData?.user?.name}
                                                imageContainerClassName="h-6 w-6"
                                                imageClassName={`w-full h-full ${
                                                  staffData.state !==
                                                    'accepted' && 'opacity-30'
                                                }`}
                                                initialClassName={`h-6 w-6 ${
                                                  staffData.state !==
                                                    'accepted' && 'opacity-30'
                                                }`}
                                              />
                                              <div
                                                className={`text-blue-950 text-sm font-medium capitalize ${
                                                  staffData.state !==
                                                    'accepted' &&
                                                  'text-opacity-30'
                                                }`}
                                              >
                                                {staffData?.user?.name}
                                              </div>
                                              <div
                                                className={`text-rose-500 text-xs font-medium  ${
                                                  staffData.state !==
                                                    'accepted' &&
                                                  'text-opacity-30'
                                                }`}
                                              >
                                                100%
                                              </div>
                                            </div>
                                          </div>
                                        </DroppableArea>
                                      </DndProvider>
                                    )
                                  )}
                                  {staff?.numberOfRemainingUsersToBeStaffed >
                                    0 &&
                                    Array.from({
                                      length:
                                        staff?.numberOfRemainingUsersToBeStaffed,
                                    })?.map((_: any, indexStaff: number) => (
                                      <DndProvider
                                        backend={HTML5Backend}
                                        key={indexStaff}
                                      >
                                        <DroppableArea
                                          onDrop={(e) =>
                                            onHandleDropMember(
                                              e,
                                              index,
                                              project,
                                              staff.roleId,
                                              staff.staffingRequirementId
                                            )
                                          }
                                          onDragOver={() => {
                                            setIsDropActive(true);
                                          }}
                                          onDragLeave={() => {
                                            setIsDropActive(false);
                                          }}
                                        >
                                          <div
                                            className={`h-10 rounded-md bg-white py-2 px-3 flex items-center justify-center ${
                                              isDropActive
                                                ? ''
                                                : 'border-dashed border-spacing-dashed'
                                            }`}
                                          >
                                            <div className="text-blue-950 text-opacity-40 text-xs font-normal">
                                              Drop here
                                            </div>
                                          </div>
                                        </DroppableArea>
                                      </DndProvider>
                                    ))}
                                </div>
                              </div>
                            </>
                          ))
                        ) : (
                          <div className="text-blue-950 text-sm font-medium capitalize">
                            No Data Found
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                );
              })}
        </div>
      </div>
      {!projectData.length && !isProjectsLoading ? (
        <div className="flex justify-center">
          <p className="text-blue-950 text-sm font-medium font-SFProDisplay cursor-pointer">
            No Project Found
          </p>
        </div>
      ) : null}
      {isModalOpen ? (
        <EditRequirementModal
          isOpen={isModalOpen}
          requiredIds={editProjectId}
          onClose={() => setIsModalOpen(false)}
        />
      ) : null}
      <ConfirmationAlertModal
        isOpen={removeMember.isOpen}
        onClose={() => {
          setRemoveMember({
            isOpen: false,
            data: {
              index: null,
              project: {},
              staffData: {},
            },
          });
        }}
        onConfirm={() =>
          onHandleRemoveMember(
            removeMember.data.index,
            removeMember.data.staffData,
            removeMember.data.project
          )
        }
        confirmationText="You want to remove this member from the project?"
        confirmationModalType=""
        modalContainerClassName="max-w-[380px] max-h-[200px]"
      />
    </div>
  );
};

export default Projects;
